/**
 * Position on map's placement canvas, reached through the renderer's chunk
 * (`retire-edit-mode-place-on-map`): `PlanSceneProviders` provides `PlanScenePlacementCanvas`, and
 * `PlanSceneLoadFailedProviders` a pane saying the plan could not be drawn. Read through this context,
 * so the step never imports three.js into the page's own chunk.
 */
import { createContext } from 'react';
import type { ComponentType } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { AdminTwin, CameraSetup, FloorPlannerLayout } from '@repo/frontend-types';
import { planDrawingOutline } from '@repo/front-end-utils';
import type { PlanDrawState } from './planDrawState';

/**
 * R2: the lowest floor's box as the placement map draws it, in floor pixels about the twin's origin.
 * The map draws no PDF underlays, so they never hold the box open: a floor with only a PDF draws
 * nothing to drag. Here, not beside the gestures, so the step can judge it without the map's chunk.
 */
export const placementOutline = planDrawingOutline;

/** The placement the map shows: the twin's stored strings, or a gesture's. */
export interface PlacementShown {
  latitude: string;
  longitude: string;
  orientation: string;
}

/**
 * What a gesture or a key run changes: the pair (a move), or the orientation with the pair (a turn).
 * A turn is about the plan's centre, not the twin's origin, so it moves the origin too and saves both
 * in one patch (`onboarding-persistent-scene-position-twin-modes`, R2.8).
 */
export type PlacementChange =
  | { kind: 'move'; latitude: string; longitude: string }
  | { kind: 'turn'; orientation: string; latitude: string; longitude: string };

export interface PlanPlacementCanvasProps {
  className?: string;
  /** The step's read of the twin. */
  twin: AdminTwin;
  twinBid: string;
  /** What the map shows: a live gesture's values over the step's baseline. */
  shown: PlacementShown;
  /** The page's working plan; `null` while it is unknown. */
  layout: FloorPlannerLayout | null;
  /** Whether the lowest floor draws something to drag (R5.4): no ring, drag or controls otherwise. */
  draggable: boolean;
  /** R10: the satellite check, owned by the step. */
  satellite: { on: boolean; opacity: number };
  /** The help lines the map element and the keyboard controls are described by (R6.1). */
  describedBy: string;
  /** Each value a gesture or a key run shows; `null` puts the saved values back. */
  onPreview: (change: PlacementChange | null) => void;
  /** Once per drag, turn or key run. */
  onCommit: (change: PlacementChange) => void;
  /** The satellite source failed (R10, Failure States). */
  onSatelliteError: (message: string) => void;
  /** The loaded map, so the step can read its camera; `null` once it is gone. */
  onMap?: (map: MapboxMap | null) => void;
  /**
   * Whether the map is out of the plan view (`onboarding-persistent-scene-position-twin-modes`,
   * R4.1): the lock `free`, or `leaving` into it. A turn or tilt gesture puts it there; the step
   * offers *Switch back to plan view* only then. Told `false` when the canvas goes.
   */
  onFreeChange?: (free: boolean) => void;
  /** R4.2: bumped by the step's *Switch back to plan view*: the lock's one move back to top-down. */
  planViewRequest?: number;
  /** R6.3: while free, what the map element is also described by (the readout and the help lines). */
  freeDescribedBy?: string;
  /**
   * Whether the map has drawn, so the step offers its camera button only then
   * (`onboarding-persistent-scene-position-twin`, R4.1). Told `false` when the canvas goes.
   */
  onDrawnChange?: (drawn: boolean) => void;
}

export const PlanPlacementRendererContext =
  createContext<ComponentType<PlanPlacementCanvasProps> | null>(null);

/** The opening view's picture (`retire-edit-mode-opening-view`, R6, R9): its size in CSS pixels. */
export const OPENING_VIEW_PICTURE_WIDTH_PX = 320;
export const OPENING_VIEW_PICTURE_HEIGHT_PX = 180;

/**
 * One picture of a saved view to draw. `id` is the twin's BID and the view's five values, so a result
 * names the view it is of.
 */
export interface OpeningViewPictureRequest {
  id: string;
  view: CameraSetup;
}

/** Why a picture was not drawn, for the one log line (spec, Access): never the view's values. */
export type OpeningViewPictureFailure =
  | 'timeout'
  | 'context-lost'
  /** The canvas could not be read into a PNG (tainted, or no image came back). */
  | 'read-failed'
  | 'map-error'
  | 'no-token'
  | 'load';

/** The one log line for a picture not drawn, with its reason only (spec, Access). */
export const OPENING_VIEW_PICTURE_FAILED_LOG =
  '[onboarding] opening view picture could not be drawn';

export type OpeningViewPictureResult =
  | { id: string; blob: Blob }
  | { id: string; failure: OpeningViewPictureFailure };

export interface OpeningViewPictureProps {
  request: OpeningViewPictureRequest;
  /** The step's read of the twin, with its saved position: where the plan is drawn. */
  twin: AdminTwin;
  twinBid: string;
  /** The page's working plan; `null` draws the basemap alone. */
  layout: FloorPlannerLayout | null;
  /** Once per request: the picture, or why there is none. */
  onResult: (result: OpeningViewPictureResult) => void;
}

/**
 * R9's hidden, picture-only map, reached through the renderer's chunk like the canvas, so the step
 * never pulls three.js into the page's own chunk. `PlanSceneLoadFailedProviders` provides one that
 * fails at once.
 */
export const PlanOpeningViewRendererContext =
  createContext<ComponentType<OpeningViewPictureProps> | null>(null);

/**
 * The washed plan behind the non-plan steps (`spec-onboarding-persistent-scene-wash`, R2): a
 * read-only layer on the onboarding stage, drawn on the page backdrop under the page's Scrim. It
 * renders no DOM of its own. Reached through the renderer's chunk like the placement canvas, so the
 * page never pulls three.js in; `PlanSceneLoadFailedProviders` provides none, so a renderer that could
 * not load draws no washed plan and the steps sit on their solid page.
 */
export interface PlanWashedLayerProps {
  /**
   * Whether the washed plan is offered (`washedPlanAvailable`, R2.4), decided once by the page on the
   * inputs it gives the stage, so the page's wash, its handover and this layer never disagree.
   */
  available: boolean;
  /** The floorplan store's key for the session plan, as Position on map is given it. */
  planScope: string | undefined;
  /** Each draw state the stage's map reports while this layer is held (R3.2). */
  onDrawState: (state: PlanDrawState) => void;
  /**
   * A one-shot request to fly the washed map to a point (`onboarding-persistent-scene-twin-details-map`,
   * R3.3): a saved address's match. Flown once per `id`, including one already waiting when the
   * layer mounts; the page stops passing it once `onFlyRequestTaken` reports it.
   */
  flyRequest?: WashedFlyRequest | null;
  /** Called with a request's `id` once the layer has flown it. */
  onFlyRequestTaken?: (id: number) => void;
}

/** A point for the washed map to fly to; a new `id` is a new request. */
export interface WashedFlyRequest {
  id: number;
  longitude: number;
  latitude: number;
}

export const PlanWashedLayerRendererContext =
  createContext<ComponentType<PlanWashedLayerProps> | null>(null);
